import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import axios from 'axios';

const ACCENT = '#2C3E2D';
const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';

function getAuthToken(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') return value;
  }
  return null;
}

const aiClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' },
});
aiClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export interface DrResortProps {
  data: Record<string, unknown>;
  context: string;
  label?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  promptPrefix?: string;
}

const POSITIONS: Record<string, React.CSSProperties> = {
  'bottom-right': { bottom: 88, right: 24 },
  'bottom-left': { bottom: 88, left: 24 },
  'top-right': { top: 24, right: 24 },
  'top-left': { top: 24, left: 24 },
};

function trimDataForAI(data: unknown): unknown {
  if (Array.isArray(data)) {
    const limited = data.slice(0, 10);
    return limited.map(item => trimItemForAI(item));
  }
  return data;
}

function trimItemForAI(item: unknown): unknown {
  if (!item || typeof item !== 'object') return item;
  const obj = item as Record<string, unknown>;
  const essential: Record<string, unknown> = {};
  const keep = [
    'name',
    'sku',
    'price',
    'cost_price',
    'status',
    'tax_rate',
    'unit_of_measure',
    'is_featured',
    'is_popular',
    'category_name',
    'current_quantity',
    'minimum_quantity',
    'reorder_point',
    'supplier_name',
    'location',
  ];
  for (const k of keep) {
    if (k === 'category_name' && obj.category && typeof obj.category === 'object') {
      essential['category'] = (obj.category as Record<string, unknown>).name;
    } else if (k === 'supplier_name' && obj.supplier && typeof obj.supplier === 'object') {
      essential['supplier'] = (obj.supplier as Record<string, unknown>).name;
    } else if (k === 'current_quantity' && obj.stock_item && typeof obj.stock_item === 'object') {
      const st = obj.stock_item as Record<string, unknown>;
      if (st.current_quantity != null) essential['stock_qty'] = st.current_quantity;
    } else if (
      obj[k] !== undefined &&
      obj[k] !== null &&
      obj[k] !== '' &&
      String(obj[k]) !== '0001-01-01T00:00:00Z'
    ) {
      essential[k] = obj[k];
    }
  }
  return essential;
}

function FloatingButton({
  onClick,
  label,
  position,
}: {
  onClick: () => void;
  label: string;
  position: string;
}) {
  const posStyle = POSITIONS[position] || POSITIONS['bottom-right'];

  return createPortal(
    <Tooltip title={`Ask ${label}`} placement="left">
      <Box
        onClick={onClick}
        sx={{
          position: 'fixed',
          ...posStyle,
          zIndex: 9999,
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: ACCENT,
          color: '#FFFFFF',
          borderRadius: '50%',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(44,62,45,0.35)',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: '#1A2C1B',
            transform: 'scale(1.1)',
            boxShadow: '0 6px 24px rgba(44,62,45,0.5)',
          },
        }}
      >
        <MedicalServicesIcon sx={{ fontSize: 24 }} />
      </Box>
    </Tooltip>,
    document.body,
  );
}

export default function DrResort({
  data,
  context,
  label = 'Dr. Resort',
  position = 'bottom-right',
  promptPrefix,
}: DrResortProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const handleAnalyze = async () => {
    setOpen(true);
    setLoading(true);
    setResult('');
    setError('');

    try {
      const trimmed = trimDataForAI(data);
      const dataStr = Array.isArray(trimmed)
        ? (trimmed as Record<string, unknown>[])
            .map(
              item =>
                `${item.name || '-'} | ${item.sku || '-'} | ${item.price || 0} KES | ${item.category || '-'}${item.is_featured ? ' | Featured' : ''}${item.is_popular ? ' | Popular' : ''}${item.status === 'unavailable' ? ' | UNAVAILABLE' : ''}${item.stock_qty != null ? ` | Qty: ${item.stock_qty}` : ''}`,
            )
            .join('\n')
        : JSON.stringify(trimmed, null, 2);
      const prompt =
        promptPrefix ||
        `Analyze this ${context} data from a resort management system. Provide insights, identify patterns, flag anomalies, and suggest actions. Be concise.`;

      const res = await aiClient.post('/api/v1/ai/refine', {
        input: dataStr,
        task: prompt,
      });

      setResult(res.data?.result || res.data?.response || 'No analysis returned.');
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          'AI analysis unavailable. Check that Ollama is running.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FloatingButton onClick={handleAnalyze} label={label} position={position} />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid #E2DDD8',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: ACCENT,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid #E2DDD8',
            px: 3,
            py: 2,
          }}
        >
          <AutoAwesomeIcon sx={{ color: '#C5A46E' }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: '#9B958F' }}>
              {context} analysis
            </Typography>
          </Box>
          <MuiIconButton onClick={() => setOpen(false)} sx={{ ml: 'auto' }}>
            <CloseIcon />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, minHeight: 200 }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={36} sx={{ color: '#C5A46E', mb: 2 }} />
              <Typography sx={{ color: '#6B6560' }}>
                {label} is analyzing the {context} data...
              </Typography>
              <Typography variant="caption" sx={{ color: '#9B958F', mt: 0.5 }}>
                Running on local CPU — this may take up to a minute for larger datasets
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#C62828', mb: 1, fontWeight: 600 }}>
                Analysis Failed
              </Typography>
              <Typography sx={{ color: '#6B6560' }}>{error}</Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${context}`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(44,62,45,0.08)',
                    color: ACCENT,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    borderRadius: 1,
                  }}
                />
                <Chip
                  label="Local AI"
                  size="small"
                  icon={<AutoAwesomeIcon sx={{ fontSize: 12, color: '#C5A46E !important' }} />}
                  sx={{
                    bgcolor: '#FFFDF7',
                    border: '1px solid #C5A46E',
                    color: '#C5A46E',
                    fontSize: '0.65rem',
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Box
                sx={{
                  bgcolor: '#FAFAF8',
                  border: '1px solid #E2DDD8',
                  borderRadius: 2,
                  p: 2.5,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.8,
                    color: '#1A1A1A',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {result}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#9B958F' }}>
                  On-premise AI • No data leaves your resort
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
